import isEqual from "lodash/isEqual";
import { memo } from "react";
import ReactMarkdown, { type Options } from "react-markdown";
import { Link } from "#/components/Link/Link";

type InlineMarkdownProps = {
	/**
	 * The Markdown text to parse and render
	 */
	children: string;

	/**
	 * Additional element types to allow.
	 * Allows italic, bold, links, and inline code snippets by default.
	 * eg. `["ol", "ul", "li"]` to support lists.
	 */
	allowedElements?: readonly string[];

	className?: string;

	/**
	 * Can override the behavior of the generated elements
	 */
	components?: Options["components"];
};

/**
 * Supports a strict subset of Markdown that behaves well as inline/confined
 * content. Separated from the full Markdown component so that importing it
 * does not pull in the heavy PrismJS syntax-highlighting bundle.
 */
export const InlineMarkdown: React.FC<InlineMarkdownProps> = (props) => {
	const { children, allowedElements = [], className, components = {} } = props;

	return (
		<ReactMarkdown
			className={className}
			allowedElements={[
				"p",
				"em",
				"strong",
				"a",
				"pre",
				"code",
				...allowedElements,
			]}
			unwrapDisallowed
			components={{
				p: ({ children }) => children,

				a: ({ href, children }) => {
					const isExternal = href?.startsWith("http");

					return (
						<Link
							href={href}
							target={isExternal ? "_blank" : undefined}
							showExternalIcon={isExternal}
							className="text-[length:inherit] p-0"
						>
							{children}
						</Link>
					);
				},

				code: ({ node, className, children, style, ...props }) => (
					<code
						className="rounded-sm bg-border px-1 py-px text-sm text-content-primary"
						{...props}
					>
						{children}
					</code>
				),

				...components,
			}}
		>
			{children}
		</ReactMarkdown>
	);
};

export const MemoizedInlineMarkdown = memo(InlineMarkdown, isEqual);
